'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  Building2,
  Home,
  MapPin,
  DollarSign,
  Upload,
  User,
  Check,
  ChevronRight,
  ChevronLeft,
  X,
  FileText,
  Video,
  Image as ImageIcon,
  Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import Script from 'next/script';

interface FormData {
  // Step 1: General Information
  listingPurpose: string; // 'For Rent' or 'For Sale'
  category: string;
  propertyType: string;

  // Step 2: Location
  projectName: string;
  generalArea: string;
  barangay: string;
  developer: string;
  towerPhase: string;
  floorStreet: string;
  unitBlockLot: string;
  mapLink: string;

  // Step 3: Property Specifics
  lotArea: string;
  floorArea: string;
  bedrooms: string;
  bathrooms: string;
  furnishing: string;
  maidsRoom: string;
  driversRoom: string;
  petsAllowed: string;
  parkingSlots: string;
  parkingSlotNo: string;
  yearBuilt: string;
  occupancyStatus: string;
  viewingAvailability: string;
  propertyOrientation: string;
  incomeGenerating: string;
  airbnbAllowed: string;
  propertyDisclosure: string;
  unitDescription: string;
  buildingDescription: string;

  // Step 4: Price & Payment
  monthlyRent: string;
  parkingPrice: string;
  acceptedRentalPeriod: string;
  acceptedPaymentTerm: string;

  // Step 5: Other Information
  titleStatus: string;
  titleStatusRemarks: string;
  ownerName: string;
  keyStatus: string;
  canBePostedOnline: string;

  // Step 6: Media Upload
  photos: File[];
  video: File | null;
  ownershipDocs: {
    title: File | null;
    contractToSale: File | null;
    deedOfAssignment: File | null;
    spa: File | null;
    other: File | null;
  };
  youtubeLink: string;
  googleDriveLink: string;

  // Step 7: Professional Fee
  listingType: string;

  // Step 8: Contact Details
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  additionalNotes: string;
}

export default function ListPropertyPage() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<'top' | 'middle' | 'bottom'>('top');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<FormData>({
    listingPurpose: '',
    category: '',
    propertyType: '',
    projectName: '',
    generalArea: '',
    barangay: '',
    developer: '',
    towerPhase: '',
    floorStreet: '',
    unitBlockLot: '',
    mapLink: '',
    lotArea: '',
    floorArea: '',
    bedrooms: '',
    bathrooms: '',
    furnishing: '',
    maidsRoom: '',
    driversRoom: '',
    petsAllowed: '',
    parkingSlots: '',
    parkingSlotNo: '',
    yearBuilt: '',
    occupancyStatus: '',
    viewingAvailability: '',
    propertyOrientation: '',
    incomeGenerating: '',
    airbnbAllowed: '',
    propertyDisclosure: '',
    unitDescription: '',
    buildingDescription: '',
    monthlyRent: '',
    parkingPrice: '0',
    acceptedRentalPeriod: '',
    acceptedPaymentTerm: '',
    titleStatus: '',
    titleStatusRemarks: '',
    ownerName: '',
    keyStatus: '',
    canBePostedOnline: '',
    photos: [],
    video: null,
    ownershipDocs: {
      title: null,
      contractToSale: null,
      deedOfAssignment: null,
      spa: null,
      other: null
    },
    youtubeLink: '',
    googleDriveLink: '',
    listingType: '',
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    additionalNotes: ''
  });

  const totalSteps = 8;

  const locations = [
    // Metro Manila Cities
    { group: 'Metro Manila - Cities', value: 'Caloocan' },
    { group: 'Metro Manila - Cities', value: 'Las Piñas' },
    { group: 'Metro Manila - Cities', value: 'Makati' },
    { group: 'Metro Manila - Cities', value: 'Malabon' },
    { group: 'Metro Manila - Cities', value: 'Mandaluyong' },
    { group: 'Metro Manila - Cities', value: 'Manila' },
    { group: 'Metro Manila - Cities', value: 'Marikina' },
    { group: 'Metro Manila - Cities', value: 'Muntinlupa' },
    { group: 'Metro Manila - Cities', value: 'Navotas' },
    { group: 'Metro Manila - Cities', value: 'Parañaque' },
    { group: 'Metro Manila - Cities', value: 'Pasay' },
    { group: 'Metro Manila - Cities', value: 'Pasig' },
    { group: 'Metro Manila - Cities', value: 'Pateros' },
    { group: 'Metro Manila - Cities', value: 'Quezon City' },
    { group: 'Metro Manila - Cities', value: 'San Juan' },
    { group: 'Metro Manila - Cities', value: 'Taguig' },
    { group: 'Metro Manila - Cities', value: 'Valenzuela' },

    // Metro Manila - Key Areas
    { group: 'Metro Manila - Key Areas', value: 'BGC (Bonifacio Global City)' },
    { group: 'Metro Manila - Key Areas', value: 'Ortigas Center' },
    { group: 'Metro Manila - Key Areas', value: 'Alabang' },
    { group: 'Metro Manila - Key Areas', value: 'Cubao' },
    { group: 'Metro Manila - Key Areas', value: 'Ermita' },
    { group: 'Metro Manila - Key Areas', value: 'Malate' },

    // Greater Manila Area / CALABARZON
    { group: 'Nearby Provinces', value: 'Cavite' },
    { group: 'Nearby Provinces', value: 'Laguna' },
    { group: 'Nearby Provinces', value: 'Batangas' },
    { group: 'Nearby Provinces', value: 'Rizal' },
    { group: 'Nearby Provinces', value: 'Bulacan' },

    // Other Major Luzon Cities
    { group: 'Other Luzon', value: 'Pampanga' },
    { group: 'Other Luzon', value: 'Tarlac' },
    { group: 'Other Luzon', value: 'Nueva Ecija' },
    { group: 'Other Luzon', value: 'Zambales' },
    { group: 'Other Luzon', value: 'Bataan' },
    { group: 'Other Luzon', value: 'Pangasinan' },
    { group: 'Other Luzon', value: 'La Union' },
    { group: 'Other Luzon', value: 'Baguio' },
    { group: 'Other Luzon', value: 'Albay' },
    { group: 'Other Luzon', value: 'Camarines Sur' },

    // Other option
    { group: 'Other', value: 'Other - Please Specify' }
  ];

  const [showOtherLocation, setShowOtherLocation] = useState(false);
  const [otherLocation, setOtherLocation] = useState('');
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [useAutocomplete, setUseAutocomplete] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState('');
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Display values for price inputs (with thousand separators)
  const [monthlyRentDisplay, setMonthlyRentDisplay] = useState('');
  const [parkingPriceDisplay, setParkingPriceDisplay] = useState('');

  // Helper function to format number with thousand separators
  const formatNumber = (value: string): string => {
    if (!value) return '';
    const number = value.replace(/,/g, '');
    if (isNaN(Number(number))) return value;
    return Number(number).toLocaleString('en-US');
  };

  // Helper function to remove formatting
  const unformatNumber = (value: string): string => {
    return value.replace(/,/g, '');
  };

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (googleMapsLoaded && useAutocomplete && autocompleteInputRef.current) {
      autocompleteRef.current = new google.maps.places.Autocomplete(
        autocompleteInputRef.current,
        {
          componentRestrictions: { country: 'ph' }, // Restrict to Philippines
          fields: ['address_components', 'formatted_address', 'geometry', 'name'],
          types: ['geocode', 'establishment']
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();
        if (place?.formatted_address) {
          setAutocompleteValue(place.formatted_address);
          setFormData({ ...formData, generalArea: place.formatted_address });
        }
      });
    }

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [googleMapsLoaded, useAutocomplete]);

  // Check if content is scrollable
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const isScrollable = container.scrollHeight > container.clientHeight;
      setShowScrollIndicator(isScrollable);

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollTop === 0) {
          setScrollPosition('top');
        } else if (scrollTop + clientHeight >= scrollHeight - 10) {
          setScrollPosition('bottom');
        } else {
          setScrollPosition('middle');
        }
      };

      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check

      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [currentStep, formData]);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      // Scroll form container to top, not window
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState<string[]>([]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const newFiles = Array.from(e.target.files);
    setUploadingPhotos(true);

    try {
      // Upload each file to MinIO (same as admin property form)
      const uploadPromises = newFiles.map(async (file) => {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        const response = await fetch('/api/upload', {
          method: 'POST',
          body: uploadFormData,
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Upload failed:', error);
          throw new Error(error.error || `Failed to upload ${file.name}`);
        }

        const result = await response.json();
        if (!result.success || !result.url) {
          throw new Error('Invalid upload response');
        }
        return result.url;
      });

      const urls = await Promise.all(uploadPromises);

      // Store both File objects (for preview) and URLs (for submission)
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newFiles]
      }));

      setUploadedPhotoUrls(prev => [...prev, ...urls]);
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload some photos. Please try again.');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        video: e.target.files![0]
      }));
    }
  };

  const [uploadedDocUrls, setUploadedDocUrls] = useState<Record<string, string>>({});

  const handleOwnershipDocUpload = async (docType: keyof typeof formData.ownershipDocs, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    try {
      // Upload PDF to MinIO (same as admin property form)
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      const result = await response.json();

      // Store both File object (for display) and URL (for submission)
      setFormData(prev => ({
        ...prev,
        ownershipDocs: {
          ...prev.ownershipDocs,
          [docType]: file
        }
      }));

      setUploadedDocUrls(prev => ({
        ...prev,
        [docType]: result.url
      }));
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(`Failed to upload ${file.name}. Please try again.`);
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/property-submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Step 1: General Information
          listingPurpose: formData.listingPurpose,
          category: formData.category,
          propertyType: formData.propertyType,

          // Step 2: Location
          projectName: formData.projectName,
          generalArea: formData.generalArea,
          barangay: formData.barangay,
          developer: formData.developer,
          towerPhase: formData.towerPhase,
          floorStreet: formData.floorStreet,
          unitBlockLot: formData.unitBlockLot,
          mapLink: formData.mapLink,
          useAutocomplete: useAutocomplete,
          autocompleteValue: autocompleteValue,
          otherLocation: otherLocation,

          // Step 3: Property Specifics
          lotArea: formData.lotArea,
          floorArea: formData.floorArea,
          bedrooms: formData.bedrooms,
          bathrooms: formData.bathrooms,
          furnishing: formData.furnishing,
          maidsRoom: formData.maidsRoom,
          driversRoom: formData.driversRoom,
          petsAllowed: formData.petsAllowed,
          parkingSlots: formData.parkingSlots,
          parkingSlotNo: formData.parkingSlotNo,
          yearBuilt: formData.yearBuilt,
          occupancyStatus: formData.occupancyStatus,
          viewingAvailability: formData.viewingAvailability,
          propertyOrientation: formData.propertyOrientation,
          incomeGenerating: formData.incomeGenerating,
          airbnbAllowed: formData.airbnbAllowed,
          propertyDisclosure: formData.propertyDisclosure,
          unitDescription: formData.unitDescription,
          buildingDescription: formData.buildingDescription,

          // Step 4: Price & Payment
          monthlyRent: formData.monthlyRent,
          parkingPrice: formData.parkingPrice,
          acceptedRentalPeriod: formData.acceptedRentalPeriod,
          acceptedPaymentTerm: formData.acceptedPaymentTerm,

          // Step 5: Other Information
          titleStatus: formData.titleStatus,
          titleStatusRemarks: formData.titleStatusRemarks,
          ownerName: formData.ownerName,
          keyStatus: formData.keyStatus,
          canBePostedOnline: formData.canBePostedOnline,

          // Step 6: Media Upload - Send MinIO URLs
          photos: uploadedPhotoUrls, // Array of MinIO URLs
          videoUrl: null, // Video files not uploaded, only links allowed
          ownershipDocs: uploadedDocUrls, // Object with MinIO URLs for each doc type
          youtubeLink: formData.youtubeLink,
          googleDriveLink: formData.googleDriveLink,

          // Step 7: Professional Fee
          listingType: formData.listingType,

          // Step 8: Contact Details
          firstName: formData.firstName,
          lastName: formData.lastName,
          mobile: formData.mobile,
          email: formData.email,
          additionalNotes: formData.additionalNotes,
        })
      });

      if (response.ok) {
        setShowSuccessModal(true);
        setTimeout(() => router.push('/'), 5000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit listing. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // OLD CODE REMOVED - Was formatting data as text message for inquiries table
  // Now sends structured data to property_submissions table
  const OLD_handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: null,
          propertyTitle: `New Listing: ${formData.category} - ${formData.propertyType}`,
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.mobile,
          lookingFor: 'owner',
          preferredContact: 'whatsapp',
          inquiryType: 'Property Listing',
          message: `
=== GENERAL INFORMATION ===
Category: ${formData.category}
Property Type: ${formData.propertyType}

=== LOCATION ===
Project: ${formData.projectName}
General Area: ${useAutocomplete ? autocompleteValue : (formData.generalArea === 'Other - Please Specify' ? otherLocation : formData.generalArea)}
Barangay: ${formData.barangay}
Developer: ${formData.developer}
Tower/Phase: ${formData.towerPhase}
Floor/Street: ${formData.floorStreet}
Unit/Block/Lot: ${formData.unitBlockLot}
Map Link: ${formData.mapLink}

=== PROPERTY SPECIFICS ===
Lot Area: ${formData.lotArea} sqm
Floor Area: ${formData.floorArea} sqm
Bedrooms: ${formData.bedrooms}
Bathrooms: ${formData.bathrooms}
Furnishing: ${formData.furnishing}
Maid's Room: ${formData.maidsRoom}
Driver's Room: ${formData.driversRoom}
Pets Allowed: ${formData.petsAllowed}
Parking Slots: ${formData.parkingSlots}
Parking Slot No: ${formData.parkingSlotNo}
Year Built: ${formData.yearBuilt}
Occupancy Status: ${formData.occupancyStatus}
Viewing Availability: ${formData.viewingAvailability}
Property Orientation: ${formData.propertyOrientation}
Income Generating: ${formData.incomeGenerating}
AirBNB Allowed: ${formData.airbnbAllowed}

Property Disclosure: ${formData.propertyDisclosure}

Unit Description:
${formData.unitDescription}

Building Description:
${formData.buildingDescription}

=== PRICE & PAYMENT ===
Monthly Rent: ₱${formData.monthlyRent}
Parking Price: ₱${formData.parkingPrice}
Accepted Rental Period: ${formData.acceptedRentalPeriod}
Accepted Payment Term: ${formData.acceptedPaymentTerm}

=== OTHER INFORMATION ===
Title Status: ${formData.titleStatus}
Title Status Remarks: ${formData.titleStatusRemarks}
Owner Name: ${formData.ownerName}
Key Status: ${formData.keyStatus}
Can Be Posted Online: ${formData.canBePostedOnline}

=== MEDIA ===
Photos Uploaded: ${formData.photos.length}
Video Uploaded: ${formData.video ? 'Yes' : 'No'}
YouTube Link: ${formData.youtubeLink}
Google Drive Link: ${formData.googleDriveLink}

=== PROFESSIONAL FEE ===
Listing Type: ${formData.listingType}

=== ADDITIONAL NOTES ===
${formData.additionalNotes}
          `,
          source: 'list-property-form'
        })
      });

      if (response.ok) {
        alert('Thank you! Your property listing has been submitted. We will contact you within 24 hours to verify details and publish your listing.');
        router.push('/');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit listing. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        // General Information
        return (
          <div className="space-y-6">
            <p className="text-gray-600 mb-6">
              Complete the form to list your property with Spire Group. Please provide as much detailed information as possible about your property.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Listing Purpose *</label>
              <div className="grid grid-cols-2 gap-4">
                {['For Rent', 'For Sale'].map((purpose) => (
                  <button
                    key={purpose}
                    type="button"
                    onClick={() => setFormData({ ...formData, listingPurpose: purpose })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.listingPurpose === purpose
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-200 hover:border-blue-300 text-gray-700'
                    }`}
                  >
                    <p className="text-base font-semibold">{purpose}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Category *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['Residential', 'Commercial', 'Industrial', 'Land', 'Other'].map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: cat })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.category === cat
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{cat}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Property Type *</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Condo', 'House & Lot', 'Residential Lot', 'Commercial Lot', 'Office Space', 'Apartment', 'Resort', 'Other'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFormData({ ...formData, propertyType: type })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.propertyType === type
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{type}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        // Location
        return (
          <div className="space-y-6">
            <p className="text-gray-600 mb-6">Tell us where your property is located</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Project (Building Name, Subdivision, Village) *</label>
              <input
                type="text"
                required
                value={formData.projectName}
                onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="e.g., The Residences at Greenbelt"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Location / General Area *</label>
                <button
                  type="button"
                  onClick={() => setUseAutocomplete(!useAutocomplete)}
                  className="text-xs text-[#d60500] hover:text-[#b50400] flex items-center gap-1"
                >
                  <Search className="w-3 h-3" />
                  {useAutocomplete ? 'Use Dropdown' : 'Search Address'}
                </button>
              </div>

              {useAutocomplete ? (
                <div className="relative">
                  <input
                    ref={autocompleteInputRef}
                    type="text"
                    required
                    value={autocompleteValue}
                    onChange={(e) => setAutocompleteValue(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                    placeholder="Search for a location in the Philippines..."
                  />
                  <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              ) : (
                <>
                  <select
                    required
                    value={formData.generalArea}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setFormData({ ...formData, generalArea: selectedValue });
                      setShowOtherLocation(selectedValue === 'Other - Please Specify');
                      if (selectedValue !== 'Other - Please Specify') {
                        setOtherLocation('');
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                  >
                    <option value="">Select Location</option>
                    {Object.entries(
                      locations.reduce((groups, location) => {
                        if (!groups[location.group]) groups[location.group] = [];
                        groups[location.group].push(location.value);
                        return groups;
                      }, {} as Record<string, string[]>)
                    ).map(([group, cities]) => (
                      <optgroup key={group} label={group}>
                        {cities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  {showOtherLocation && (
                    <div className="mt-3">
                      <input
                        type="text"
                        required
                        value={otherLocation}
                        onChange={(e) => setOtherLocation(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                        placeholder="Please specify the location..."
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Barangay</label>
              <input
                type="text"
                value={formData.barangay}
                onChange={(e) => setFormData({ ...formData, barangay: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="e.g., Poblacion (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Developer</label>
              <input
                type="text"
                value={formData.developer}
                onChange={(e) => setFormData({ ...formData, developer: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="e.g., Ayala Land"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tower / Phase</label>
              <input
                type="text"
                value={formData.towerPhase}
                onChange={(e) => setFormData({ ...formData, towerPhase: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="e.g., Tower 1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Floor / Street *</label>
              <input
                type="text"
                required
                value={formData.floorStreet}
                onChange={(e) => setFormData({ ...formData, floorStreet: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="e.g., 15th Floor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unit / Block / Lot No. *</label>
              <input
                type="text"
                required
                value={formData.unitBlockLot}
                onChange={(e) => setFormData({ ...formData, unitBlockLot: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="e.g., Unit 1501"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Map Link (Waze or Google)</label>
              <input
                type="url"
                value={formData.mapLink}
                onChange={(e) => setFormData({ ...formData, mapLink: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="https://maps.google.com/..."
              />
            </div>
          </div>
        );

      case 3:
        // Property Specifics
        return (
          <div className="space-y-6">
            <p className="text-gray-600 mb-6">Tell us about the property details</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lot Area (sqm)</label>
              <input
                type="number"
                value={formData.lotArea}
                onChange={(e) => setFormData({ ...formData, lotArea: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unit Area / Floor Area (sqm) *</label>
              <input
                type="number"
                required
                value={formData.floorArea}
                onChange={(e) => setFormData({ ...formData, floorArea: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Number of Bedrooms *</label>
              <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                {['Studio', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, bedrooms: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.bedrooms === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Toilet and Bath / Powder Room *</label>
              <div className="grid grid-cols-5 md:grid-cols-6 gap-3">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, bathrooms: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.bathrooms === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Furnishings *</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Bare', 'Semi-Furnished', 'Fully Furnished', 'Developer Finished'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, furnishing: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.furnishing === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Maid's Room *</label>
              <div className="grid grid-cols-2 gap-3">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, maidsRoom: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.maidsRoom === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Driver's Room *</label>
              <div className="grid grid-cols-2 gap-3">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, driversRoom: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.driversRoom === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Pets Allowed *</label>
              <div className="grid grid-cols-2 gap-3">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, petsAllowed: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.petsAllowed === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Parking Slots *</label>
              <div className="grid grid-cols-5 md:grid-cols-6 gap-3">
                {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, parkingSlots: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.parkingSlots === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parking Slot No.</label>
              <input
                type="text"
                value={formData.parkingSlotNo}
                onChange={(e) => setFormData({ ...formData, parkingSlotNo: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="e.g., P-45"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year Built</label>
              <input
                type="number"
                value={formData.yearBuilt}
                onChange={(e) => setFormData({ ...formData, yearBuilt: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="2020"
                min="1900"
                max={new Date().getFullYear() + 5}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Occupancy Status *</label>
              <div className="grid grid-cols-2 gap-3">
                {['Vacant', 'Occupied by Owner', 'Tenanted', 'Under Construction'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, occupancyStatus: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.occupancyStatus === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Viewing Availability *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['Anytime', 'By Appointment', 'Weekdays Only', 'Weekends Only', 'Virtual Only'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, viewingAvailability: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.viewingAvailability === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Property Orientation</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['North', 'South', 'East', 'West', 'Northeast', 'Northwest', 'Southeast', 'Southwest'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, propertyOrientation: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.propertyOrientation === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Income Generating? *</label>
              <div className="grid grid-cols-2 gap-3">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, incomeGenerating: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.incomeGenerating === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">AirBNB? *</label>
              <div className="grid grid-cols-2 gap-3">
                {['Allowed', 'Not Allowed'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, airbnbAllowed: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.airbnbAllowed === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Disclosure (issues like tumbok, perimeter, flooded, etc.)</label>
              <textarea
                value={formData.propertyDisclosure}
                onChange={(e) => setFormData({ ...formData, propertyDisclosure: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent resize-none"
                placeholder="Disclose any known issues with the property..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unit Description</label>
              <textarea
                value={formData.unitDescription}
                onChange={(e) => setFormData({ ...formData, unitDescription: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent resize-none"
                placeholder="Describe the unit features, layout, condition, etc..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Building Description</label>
              <textarea
                value={formData.buildingDescription}
                onChange={(e) => setFormData({ ...formData, buildingDescription: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent resize-none"
                placeholder="Describe the building, amenities, security, accessibility, etc..."
              />
            </div>
          </div>
        );

      case 4:
        // Price & Payment
        return (
          <div className="space-y-6">
            <p className="text-gray-600 mb-6">Tell us about your pricing expectations</p>

            {/* For Rent - Monthly Rent */}
            {formData.listingPurpose === 'For Rent' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Rental Price (₱) *</label>
                <input
                  type="text"
                  required
                  value={monthlyRentDisplay}
                  onChange={(e) => {
                    const value = unformatNumber(e.target.value);
                    setFormData({ ...formData, monthlyRent: value });
                    setMonthlyRentDisplay(formatNumber(value));
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                  placeholder="50,000"
                />
                {monthlyRentDisplay && (
                  <p className="text-xs text-gray-500 mt-1">₱{monthlyRentDisplay}</p>
                )}
              </div>
            )}

            {/* For Sale - Sale Price */}
            {formData.listingPurpose === 'For Sale' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sale Price (₱) *</label>
                <input
                  type="text"
                  required
                  value={monthlyRentDisplay}
                  onChange={(e) => {
                    const value = unformatNumber(e.target.value);
                    setFormData({ ...formData, monthlyRent: value });
                    setMonthlyRentDisplay(formatNumber(value));
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="15,000,000"
                />
                {monthlyRentDisplay && (
                  <p className="text-xs text-gray-500 mt-1">₱{monthlyRentDisplay}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parking Price (₱) - 0 if inclusive *</label>
              <input
                type="text"
                required
                value={parkingPriceDisplay}
                onChange={(e) => {
                  const value = unformatNumber(e.target.value);
                  setFormData({ ...formData, parkingPrice: value });
                  setParkingPriceDisplay(formatNumber(value));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="0"
              />
              {parkingPriceDisplay && parkingPriceDisplay !== '0' && (
                <p className="text-xs text-gray-500 mt-1">₱{parkingPriceDisplay}</p>
              )}
            </div>

            {/* Rental-specific fields */}
            {formData.listingPurpose === 'For Rent' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Accepted Rental Period *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  'Minimum 6 Months',
                  'Minimum 1 Year',
                  'Minimum 2 Year',
                  'Minimum 3 Year',
                  'Minimum 5 Year'
                ].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, acceptedRentalPeriod: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.acceptedRentalPeriod === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Accepted Payment Term For Rent *</label>
              <div className="grid grid-cols-1 gap-3">
                {[
                  '1 Month Advance - 1 Month Security',
                  '2 Month Advance - 2 Month Security (Most Popular)',
                  '3 Month Advance - 3 Month Security',
                  '6 Month Advance - 6 Month Security',
                  '12 Month Advance - 2 Month Security'
                ].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, acceptedPaymentTerm: option })}
                    className={`p-3 border-2 rounded-lg transition-all text-left ${
                      formData.acceptedPaymentTerm === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
                </div>
              </>
            )}
          </div>
        );

      case 5:
        // Other Information
        return (
          <div className="space-y-6">
            <p className="text-gray-600 mb-6">Tell us about additional property details</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Title Status *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  'Clean Title',
                  'With Developer',
                  'With Bank',
                  'Special Title',
                  'No Title'
                ].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, titleStatus: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.titleStatus === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title Status Remarks</label>
              <input
                type="text"
                value={formData.titleStatusRemarks}
                onChange={(e) => setFormData({ ...formData, titleStatusRemarks: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="Additional details about title status..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Owner's Name *</label>
              <input
                type="text"
                required
                value={formData.ownerName}
                onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="Full name as it appears on title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Key Status *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  'With Me',
                  'With Tenant',
                  'With Building Admin',
                  'With My Representative',
                  'Keys Will Be Provided',
                  'Not Yet Turned Over (Pre-Selling)'
                ].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, keyStatus: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.keyStatus === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Can be posted online/offline platforms *</label>
              <div className="grid grid-cols-2 gap-3">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormData({ ...formData, canBePostedOnline: option })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.canBePostedOnline === option
                        ? 'border-[#d60500] bg-red-50 text-[#d60500]'
                        : 'border-gray-200 hover:border-[#d60500] text-gray-700'
                    }`}
                  >
                    <p className="text-sm font-medium">{option}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 6:
        // Media Upload
        return (
          <div className="space-y-6">
            <p className="text-gray-600 mb-6">Upload photos and videos of your property</p>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Property Pictures *</h3>
              <p className="text-sm text-gray-600 mb-4">Upload high-quality images to attract more interest</p>

              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-cyan-400 transition-all">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadingPhotos ? (
                    <>
                      <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mb-3" />
                      <p className="text-sm text-cyan-600 font-semibold">Uploading photos...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mb-3" />
                      <p className="mb-2 text-sm text-gray-600">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG up to 10MB each</p>
                      <p className="text-xs text-green-600 mt-2">✓ Auto-uploads to cloud storage</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploadingPhotos}
                />
              </label>

              {formData.photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Video Links Only - No File Upload */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Property Video (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">
                Provide YouTube or Google Drive video links. Video files are too large to upload.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">YouTube Link / Reels Video</label>
                  <input
                    type="url"
                    value={formData.youtubeLink}
                    onChange={(e) => setFormData({ ...formData, youtubeLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Google Drive Video Link</label>
                  <input
                    type="url"
                    value={formData.googleDriveLink}
                    onChange={(e) => setFormData({ ...formData, googleDriveLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                    placeholder="https://drive.google.com/file/d/..."
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Ownership Documents</h3>
              <p className="text-sm text-gray-600 mb-4">Please upload at least one ownership document</p>

              <div className="space-y-3">
                {[
                  { key: 'title', label: 'Property Title' },
                  { key: 'contractToSale', label: 'Contract To Sale' },
                  { key: 'deedOfAssignment', label: 'Deed Of Assignment' },
                  { key: 'spa', label: 'Special Power of Attorney (SPA)' },
                  { key: 'other', label: 'Other Ownership Document' }
                ].map((doc) => (
                  <div key={doc.key} className="flex items-center gap-3">
                    <label className="flex-1">
                      <div className="flex items-center justify-between p-3 border-2 border-gray-200 rounded-lg hover:border-[#d60500] cursor-pointer transition-all">
                        <span className="text-sm font-medium text-gray-700">{doc.label}</span>
                        <div className="flex items-center gap-2">
                          {formData.ownershipDocs[doc.key as keyof typeof formData.ownershipDocs] && (
                            <Check className="w-5 h-5 text-cyan-500" />
                          )}
                          <Upload className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleOwnershipDocUpload(doc.key as keyof typeof formData.ownershipDocs, e)}
                        className="hidden"
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 7:
        // Professional Fee
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {formData.listingPurpose === 'For Rent' ? 'Commission structure for lease listings' : 'Commission structure for sale listings'}
            </h3>

            <div className="bg-red-50 border border-cyan-200 rounded-xl p-6">
              <p className="text-gray-800 mb-4">
                <strong>Dear Property Owner,</strong>
              </p>
              <p className="text-gray-700 mb-4">
                At Spire Group, we've observed that properties listed under an exclusive authority receive greater marketing exposure, faster results, and the full support of our broker network.
              </p>
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-gray-900 mb-2">Commission Structure:</p>
                {formData.listingPurpose === 'For Rent' ? (
                  <>
                    <p className="text-gray-700 mb-2">1 Month's Rent for a 1-year lease term</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Exclusive Authority to Lease:</strong> 1 Month Commission</li>
                      <li><strong>Non-Exclusive Authority to Lease:</strong> 1 Month Commission</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="text-gray-700 mb-2">Based on Sale Price</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Exclusive Authority to Sell:</strong> 4% Commission</li>
                      <li><strong>Non-Exclusive Authority to Sell:</strong> 5% Commission</li>
                    </ul>
                  </>
                )}
              </div>
              <p className="text-gray-700">
                Best regards,<br />
                <strong>The Spire Group Team</strong>
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Your Listing Type: *</h3>
              <div className="grid grid-cols-1 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, listingType: 'exclusive' })}
                  className={`p-4 border-2 rounded-xl transition-all text-left ${
                    formData.listingType === 'exclusive'
                      ? 'border-[#d60500] bg-red-50'
                      : 'border-gray-200 hover:border-[#d60500]'
                  }`}
                >
                  <p className={`font-semibold mb-1 ${
                    formData.listingType === 'exclusive' ? 'text-[#d60500]' : 'text-gray-900'
                  }`}>
                    {formData.listingPurpose === 'For Rent' ? 'Exclusive Authority to Lease' : 'Exclusive Authority to Sell'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {formData.listingPurpose === 'For Rent' ? '1 Month Commission' : '4% Commission'} - Priority marketing & faster results
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, listingType: 'non-exclusive' })}
                  className={`p-4 border-2 rounded-xl transition-all text-left ${
                    formData.listingType === 'non-exclusive'
                      ? 'border-[#d60500] bg-red-50'
                      : 'border-gray-200 hover:border-[#d60500]'
                  }`}
                >
                  <p className={`font-semibold mb-1 ${
                    formData.listingType === 'non-exclusive' ? 'text-[#d60500]' : 'text-gray-900'
                  }`}>
                    {formData.listingPurpose === 'For Rent' ? 'Non-Exclusive Authority to Lease' : 'Non-Exclusive Authority to Sell'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {formData.listingPurpose === 'For Rent' ? '1 Month Commission' : '5% Commission'} - Standard marketing approach
                  </p>
                </button>
              </div>
            </div>
          </div>
        );

      case 8:
        // Contact Details
        return (
          <div className="space-y-6">
            <p className="text-gray-600 mb-6">How can we reach you?</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="Juan"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="Dela Cruz"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
              <input
                type="tel"
                required
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                placeholder="+63 912 345 6789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                  placeholder="juan@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes (Optional)</label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent resize-none"
                placeholder="Any other information you'd like to share..."
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Your listing will be reviewed by our team before going live. We will contact you within 24 hours to verify details and publish your property.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.category && formData.propertyType;
      case 2:
        return formData.projectName &&
               (useAutocomplete ? autocompleteValue : formData.generalArea) &&
               formData.floorStreet &&
               formData.unitBlockLot;
      case 3:
        return (
          formData.floorArea &&
          formData.bedrooms &&
          formData.bathrooms &&
          formData.furnishing &&
          formData.maidsRoom &&
          formData.driversRoom &&
          formData.petsAllowed &&
          formData.parkingSlots &&
          formData.occupancyStatus &&
          formData.viewingAvailability &&
          formData.incomeGenerating &&
          formData.airbnbAllowed
        );
      case 4:
        // For Rent: require rental fields
        if (formData.listingPurpose === 'For Rent') {
          return (
            formData.monthlyRent &&
            formData.parkingPrice !== '' &&
            formData.acceptedRentalPeriod &&
            formData.acceptedPaymentTerm
          );
        }
        // For Sale: only require price fields
        return formData.monthlyRent && formData.parkingPrice !== '';
      case 5:
        return (
          formData.titleStatus &&
          formData.ownerName &&
          formData.keyStatus &&
          formData.canBePostedOnline
        );
      case 6:
        return formData.photos.length > 0;
      case 7:
        return formData.listingType;
      case 8:
        return (
          formData.firstName &&
          formData.lastName &&
          formData.mobile &&
          formData.email
        );
      default:
        return false;
    }
  };

  return (
    <>
      {/* Proper UX Architecture - Form always centered, footer below viewport */}
      <div className="min-h-screen flex flex-col bg-gray-50">

        {/* Main Content Area - Flex-grow centers the form */}
        <main className="flex-1 flex items-center justify-center py-4 md:py-6">

          {/* Form Container - Scrollable with max height */}
          <div
            ref={scrollContainerRef}
            className="w-full max-w-2xl px-4 max-h-[85vh] overflow-y-auto relative scroll-smooth"
          >

            {/* Header - Compact */}
            <div className="text-center mb-4 md:mb-6">
              <div className="flex justify-center mb-3">
                <Image
                  src="/spire-logo-black.webp"
                  alt="Spire Group"
                  width={120}
                  height={36}
                  className="h-10 w-auto"
                />
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 font-heading">
                List Your Property
              </h1>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Step {currentStep} of {totalSteps}</span>
                <span className="text-sm font-medium text-[#d60500]">{Math.round((currentStep / totalSteps) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            {/* Scroll Indicators */}
            {showScrollIndicator && scrollPosition !== 'bottom' && (
              <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30 animate-bounce pointer-events-none">
                <div className="bg-[#d60500] text-white rounded-full p-2 shadow-lg">
                  <ChevronRight className="w-5 h-5 rotate-90" />
                </div>
              </div>
            )}

            {showScrollIndicator && scrollPosition !== 'top' && (
              <div className="fixed top-32 left-1/2 -translate-x-1/2 z-30 animate-bounce pointer-events-none">
                <div className="bg-gray-700 text-white rounded-full p-2 shadow-lg">
                  <ChevronRight className="w-5 h-5 -rotate-90" />
                </div>
              </div>
            )}

            {/* Form Card - No min-height hacks */}
            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4 relative">
              {/* Step Title */}
              <div className="mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {currentStep === 1 && 'General Information'}
                  {currentStep === 2 && 'Location'}
                  {currentStep === 3 && 'Property Specifics'}
                  {currentStep === 4 && 'Price & Payment'}
                  {currentStep === 5 && 'Other Information'}
                  {currentStep === 6 && 'Media Upload'}
                  {currentStep === 7 && 'Professional Fee'}
                  {currentStep === 8 && 'Contact Details'}
                </h2>
              </div>

              {/* Step Content - No wrapper, direct render */}
              {renderStep()}

              {/* Scroll fade indicator at bottom */}
              {showScrollIndicator && scrollPosition !== 'bottom' && (
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/50 to-transparent pointer-events-none" />
              )}
            </div>

            {/* Navigation Buttons - Always visible at bottom of scrollable area */}
            <div className="flex justify-between gap-4 pb-4 sticky bottom-0 bg-gray-50 py-4">
              <Button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 1}
                variant="outline"
                className="px-6 py-3 text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                BACK
              </Button>

              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="px-6 py-3 bg-red-500 hover:bg-[#b50400] text-white"
                >
                  CONTINUE
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canProceed() || isSubmitting}
                  className="px-6 py-3 bg-red-500 hover:bg-[#b50400] text-white"
                >
                  {isSubmitting ? 'Submitting...' : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Submit Property
                    </>
                  )}
                </Button>
              )}
            </div>

          </div>
        </main>

        {/* Footer - Outside main, below viewport */}
        <footer className="bg-white border-t py-6">
          <div className="max-w-2xl mx-auto px-4 text-center text-sm text-gray-600">
            <p>© {new Date().getFullYear()} Spire Group. Professional Real Estate Services.</p>
          </div>
        </footer>

      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            {/* Success Header with Gradient */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 px-8 py-12 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Check className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">
                Submission Successful!
              </h2>
              <p className="text-green-50 text-lg">
                Your property listing has been received
              </p>
            </div>

            {/* Content */}
            <div className="px-8 py-8 space-y-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-lg">📧</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">What happens next?</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Our team will review your submission
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        We'll contact you within 24 hours
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Your property will be published after verification
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Redirecting to homepage in <span className="font-semibold text-green-600">5 seconds...</span>
                </p>
                <button
                  onClick={() => router.push('/')}
                  className="text-sm text-[#d60500] hover:text-[#b50400] font-medium"
                >
                  Return to homepage now →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Maps Script for Autocomplete */}
      <Script
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY'}&libraries=places`}
        onLoad={() => setGoogleMapsLoaded(true)}
        strategy="lazyOnload"
      />
    </>
  );
}
